import React from "react";
import type { DataTableRowDragHandlers } from "../DataTable.types";
import {
  TableRow,
  TableCell,
  Checkbox,
  Tooltip,
  Box,
  Collapse,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import type { DataTableColumn, DataTableRowControls } from "@ui/DataTable";
import {
  COLUMN_WIDTHS,
  TABLE_Z_INDEX,
  PINNED_SHADOWS,
  defaultDataTableTheme,
  getDensityPaddingWithLeft,
  ROW_CONTROLS_LAYOUT,
} from "../DataTable.theme";
import { MelaButton } from "@/ui";

interface DataTableRowProps<TRow> {
  row: TRow;
  rowIndex: number;
  rowKey: string;
  visibleColumns: DataTableColumn<TRow>[];
  selectable: boolean;
  selectionColumnWidth: number;
  rowControls?: DataTableRowControls<TRow>;
  rowControlsSlots: number;
  rowControlsWidth: number;
  selected: boolean;
  highlighted: boolean;
  onSelectRow: (id: string) => void;
  expandable: boolean;
  showExpandColumn: boolean;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  getDetailPanelContent?: (row: TRow) => React.ReactNode;
  isArchived: boolean;
  onRowClick?: (row: TRow, event: React.MouseEvent) => void;
  onRowDoubleClick?: (row: TRow, event: React.MouseEvent) => void;
  rowClassName?: (row: TRow) => string;
  dragHandlers?: DataTableRowDragHandlers;
  classes: any;
  densityStyles: any;
  themeStyles: any;
  getColumnWidth: (
    column: DataTableColumn<TRow>,
  ) => number | string | undefined;
  getPinnedLeftOffset: (index: number) => number;
  getPinnedRightOffset: (index: number) => number;
  pinnedLeft: DataTableColumn<TRow>[];
  pinnedRight: DataTableColumn<TRow>[];
  lastPinnedLeftKey: string | null;
  firstPinnedRightKey: string | null;
  isScrolledLeft: boolean;
  isScrolledRight: boolean;
  isResizing: boolean;
  disableCellLayoutAnimation: boolean;
  totalColumnCount: number;
  renderCellContent: (
    row: TRow,
    column: DataTableColumn<TRow>,
    rowIndex: number,
  ) => React.ReactNode;
  getCellClassName: (
    row: TRow,
    column: DataTableColumn<TRow>,
    colIndex: number,
  ) => string;
}

const MotionTableCell = motion.create(TableCell);

const DataTableRowInternal = <TRow,>({
  row,
  rowIndex,
  rowKey,
  visibleColumns,
  selectable,
  selectionColumnWidth,
  rowControls,
  rowControlsSlots,
  rowControlsWidth,
  selected,
  highlighted,
  onSelectRow,
  expandable,
  showExpandColumn,
  isExpanded,
  onToggleExpand,
  getDetailPanelContent,
  isArchived,
  onRowClick,
  onRowDoubleClick,
  rowClassName,
  dragHandlers,
  classes,
  densityStyles,
  themeStyles,
  getColumnWidth,
  getPinnedLeftOffset,
  getPinnedRightOffset,
  pinnedLeft,
  pinnedRight,
  lastPinnedLeftKey,
  firstPinnedRightKey,
  isScrolledLeft,
  isScrolledRight,
  isResizing,
  disableCellLayoutAnimation,
  totalColumnCount,
  renderCellContent,
  getCellClassName,
}: DataTableRowProps<TRow>) => {
  const { t } = useTranslation();
  const isClickable = !!onRowClick || !!onRowDoubleClick;
  const isSelectedOrHighlighted = selected || highlighted;
  const shouldAnimateCellLayout = !disableCellLayoutAnimation && !isResizing;
  const cellTransition = shouldAnimateCellLayout
    ? {
        type: "spring" as const,
        stiffness: 300,
        damping: 30,
      }
    : { duration: 0 };
  const selectionColumnPadding = Math.max(
    0,
    selectionColumnWidth - COLUMN_WIDTHS.checkbox,
  );

  return (
    <React.Fragment>
      <TableRow
        onDragOver={dragHandlers?.onDragOver}
        onDrop={dragHandlers?.onDrop}
        onDragEnd={dragHandlers?.onDragEnd}
        hover
        selected={selected}
        className={`${classes.tableRow} ${highlighted ? classes.highlightedRow : ""} ${rowClassName ? rowClassName(row) : ""} ${isExpanded ? "Mui-expanded" : ""} ${isArchived ? classes.archivedRow : ""}`}
        onClick={(e) => onRowClick?.(row, e)}
        onDoubleClick={(e) => onRowDoubleClick?.(row, e)}
        style={{ cursor: isClickable ? "pointer" : "default" }}
        sx={{
          ...themeStyles.row,
          ...(isExpanded && {
            "& td": { borderBottom: "none" },
            backgroundColor: isSelectedOrHighlighted
              ? defaultDataTableTheme.rowSelectedBg
              : defaultDataTableTheme.rowHoverBg,
          }),
        }}
      >
        {/* Selection checkbox in row */}
        {selectable && (
          <MotionTableCell
            layout={shouldAnimateCellLayout}
            transition={cellTransition}
            key="__select__"
            padding="none"
            className={`${classes.tableCell} ${classes.pinnedCell}`}
            style={{
              ...densityStyles,
              boxSizing: "border-box",
              width: selectionColumnWidth,
              minWidth: selectionColumnWidth,
              maxWidth: selectionColumnWidth,
              padding: `0 0 0 ${selectionColumnPadding}px`,
              overflow: "hidden",
              textOverflow: "clip",
              whiteSpace: "nowrap",
              position: "sticky",
              left: 0,
              zIndex: TABLE_Z_INDEX.pinnedBody,
              ...(isScrolledLeft &&
                lastPinnedLeftKey === "__select__" && {
                  boxShadow: PINNED_SHADOWS.left,
                }),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {isArchived && (
              <Tooltip title={t("task.archived.translation", "Archived")}>
                <Box className={classes.archivedIndicator} />
              </Tooltip>
            )}
            <Checkbox
              checked={selected}
              onChange={() => onSelectRow(rowKey)}
              size="small"
              sx={{ padding: "4px" }}
              disableRipple
              disableFocusRipple
            />
          </MotionTableCell>
        )}
        {/* Structural controls in a stable fixed-slot grid */}
        {rowControls && (
          <MotionTableCell
            layout={shouldAnimateCellLayout}
            transition={cellTransition}
            key="__row_controls__"
            padding="none"
            className={`${classes.tableCell} ${classes.pinnedCell}`}
            aria-label={rowControls.ariaLabel}
            data-testid={`data-table-row-controls-${rowKey}`}
            style={{
              ...densityStyles,
              boxSizing: "border-box",
              width: rowControlsWidth,
              minWidth: rowControlsWidth,
              maxWidth: rowControlsWidth,
              padding: 0,
              overflow: "hidden",
              textOverflow: "clip",
              whiteSpace: "nowrap",
              position: "sticky",
              left: selectable ? selectionColumnWidth : 0,
              zIndex: TABLE_Z_INDEX.pinnedBody,
              ...(isScrolledLeft &&
                lastPinnedLeftKey === "__row_controls__" && {
                  boxShadow: PINNED_SHADOWS.left,
                }),
            }}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            <Box
              sx={{
                alignItems: "center",
                columnGap: `${ROW_CONTROLS_LAYOUT.gap}px`,
                display: "grid",
                gridTemplateColumns: `repeat(${rowControlsSlots}, ${ROW_CONTROLS_LAYOUT.slotWidth}px)`,
                justifyItems: "center",
                minHeight: ROW_CONTROLS_LAYOUT.slotWidth,
                paddingInline: `${ROW_CONTROLS_LAYOUT.paddingInline}px`,
                position: "relative",
              }}
            >
              {rowControls.render(row, rowIndex)}
            </Box>
          </MotionTableCell>
        )}
        {/* Expand button */}
        {expandable && showExpandColumn && (
          <MotionTableCell
            layout={shouldAnimateCellLayout}
            transition={cellTransition}
            key="__expand__"
            padding="none"
            className={classes.pinnedCell}
            style={{
              ...densityStyles,
              boxSizing: "border-box",
              width:
                COLUMN_WIDTHS.checkbox +
                (!selectable && !rowControls
                  ? COLUMN_WIDTHS.firstColumnPadding
                  : 0),
              minWidth:
                COLUMN_WIDTHS.checkbox +
                (!selectable && !rowControls
                  ? COLUMN_WIDTHS.firstColumnPadding
                  : 0),
              maxWidth:
                COLUMN_WIDTHS.checkbox +
                (!selectable && !rowControls
                  ? COLUMN_WIDTHS.firstColumnPadding
                  : 0),
              padding:
                selectable || rowControls
                  ? 0
                  : `0 0 0 ${COLUMN_WIDTHS.firstColumnPadding}px`,
              overflow: "hidden",
              textOverflow: "clip",
              whiteSpace: "nowrap",
              position: "sticky",
              left: (selectable ? selectionColumnWidth : 0) + rowControlsWidth,
              zIndex: TABLE_Z_INDEX.pinnedBody,
              ...(isScrolledLeft &&
                lastPinnedLeftKey === "__expand__" && {
                  boxShadow: PINNED_SHADOWS.left,
                }),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {isArchived && !selectable && (
              <Tooltip title={t("task.archived.translation", "Archived")}>
                <Box className={classes.archivedIndicator} />
              </Tooltip>
            )}
            <MelaButton
              iconOnly
              tone="neutral"
              variant="ghost"
              size="sm"
              onClick={() => onToggleExpand(rowKey)}
              aria-label={isExpanded ? "collapse row" : "expand row"}
              startIcon={
                isExpanded ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )
              }
              style={{
                minWidth: 28,
                width: 28,
                height: 28,
                padding: 4,
              }}
            />
          </MotionTableCell>
        )}
        {visibleColumns.map((column, colIndex) => {
          const hasFirstColumnPadding =
            colIndex === 0 &&
            !selectable &&
            !rowControls &&
            (!expandable || !showExpandColumn);
          const firstColumnPadding = hasFirstColumnPadding
            ? COLUMN_WIDTHS.firstColumnPadding
            : 0;
          const minWidth =
            column.minWidth !== undefined
              ? column.minWidth + firstColumnPadding
              : undefined;
          const maxWidth =
            column.maxWidth !== undefined
              ? column.maxWidth + firstColumnPadding
              : undefined;

          return (
            <MotionTableCell
              layout={shouldAnimateCellLayout}
              transition={cellTransition}
              key={column.key}
              padding="none"
              className={`${getCellClassName(row, column, colIndex)} ${column.pinned ? classes.pinnedCell : ""}`}
              align={column.align || "left"}
              style={{
                width:
                  typeof getColumnWidth(column) === "number"
                    ? (getColumnWidth(column) as number) +
                      (hasFirstColumnPadding
                        ? COLUMN_WIDTHS.firstColumnPadding
                        : 0)
                    : getColumnWidth(column),
                minWidth,
                maxWidth,
                ...densityStyles,
                padding: hasFirstColumnPadding
                  ? getDensityPaddingWithLeft(
                      densityStyles.padding,
                      COLUMN_WIDTHS.firstColumnPadding,
                    )
                  : densityStyles.padding,
                ...(column.pinned === "left" && {
                  position: "sticky" as const,
                  left: getPinnedLeftOffset(pinnedLeft.indexOf(column)),
                  zIndex: TABLE_Z_INDEX.pinnedBody,
                  ...(isScrolledLeft &&
                    lastPinnedLeftKey === column.key && {
                      boxShadow: PINNED_SHADOWS.left,
                    }),
                }),
                ...(column.pinned === "right" && {
                  position: "sticky" as const,
                  right: getPinnedRightOffset(pinnedRight.indexOf(column)),
                  zIndex: TABLE_Z_INDEX.pinnedBody,
                  ...(isScrolledRight &&
                    firstPinnedRightKey === column.key && {
                      boxShadow: PINNED_SHADOWS.right,
                    }),
                }),
              }}
            >
              {isArchived &&
                !selectable &&
                (!expandable || !showExpandColumn) &&
                colIndex === 0 && (
                  <Tooltip title={t("task.archived.translation", "Archived")}>
                    <Box className={classes.archivedIndicator} />
                  </Tooltip>
                )}
              {renderCellContent(row, column, rowIndex)}
            </MotionTableCell>
          );
        })}
      </TableRow>
      {/* Expanded detail panel */}
      {expandable && getDetailPanelContent && (
        <TableRow
          selected={selected}
          className={`${classes.tableRow} ${highlighted ? classes.highlightedRow : ""} ${isExpanded ? "Mui-expanded" : ""} ${isArchived ? classes.archivedRow : ""}`}
          onClick={(e) => onRowClick?.(row, e)}
          onDoubleClick={(e) => onRowDoubleClick?.(row, e)}
          style={{ cursor: isClickable ? "pointer" : "default" }}
          sx={{
            ...themeStyles.row,
            backgroundColor: isSelectedOrHighlighted
              ? `${defaultDataTableTheme.rowSelectedBg} !important`
              : isExpanded
                ? defaultDataTableTheme.rowHoverBg
                : "inherit",
            "&:hover": {
              backgroundColor: isSelectedOrHighlighted
                ? `${defaultDataTableTheme.rowSelectedHoverBg} !important`
                : `${defaultDataTableTheme.rowHoverBg} !important`,
            },
            "& td": {
              backgroundColor: "inherit !important",
            },
          }}
        >
          <TableCell
            colSpan={totalColumnCount}
            className={classes.tableCell}
            style={{
              paddingBottom: 0,
              paddingTop: 0,
              borderBottom: isExpanded
                ? `1px solid ${defaultDataTableTheme.borderColor}`
                : "none",
            }}
          >
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {getDetailPanelContent(row)}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

export const DataTableRow = React.memo(DataTableRowInternal) as <TRow>(
  props: DataTableRowProps<TRow>,
) => React.ReactElement;
